import { FlatFileTransform } from '@cumu/shared';
import { controller, target } from '@github/catalyst';
import { Attachment } from '@github/file-attachment-element';
import { readCsvFile } from '../csv';
import { readExcelFile } from '../excel';
import { clearValidationErrors } from '../forms/validation';
import { dispatchChange } from './util';

@controller('excel-attachment')
class ExcelAttachmentElement extends HTMLElement {
  @target declare fileInput: HTMLInputElement;
  @target declare valueInput: HTMLInputElement;
  @target declare filenameInput: HTMLInputElement;

  async attachmentAccepted(event: CustomEvent<{ attachments: Attachment[] }>) {
    const attachments = event.detail.attachments;
    const autoSubmit = this.getAttribute('auto-submit') !== 'false';
    this.valueInput.value = '';
    this.filenameInput.value = '';
    clearValidationErrors(this.valueInput);
    clearValidationErrors(this.filenameInput);
    try {
      this.fileInput.disabled = true;
      this.fileInput.labels?.[0].classList.add('btn-busy');
      const file = attachments[0].file;
      const transform: FlatFileTransform | undefined = this.hasAttribute(
        'transform'
      )
        ? JSON.parse(this.getAttribute('transform')!)
        : undefined;
      const result =
        file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
          ? await readCsvFile(
              await file.text(),
              file.name,
              this.valueInput.form!,
              this.getAttribute('lower-case-keys') === 'true',
              transform
            )
          : await readExcelFile(
              await file.arrayBuffer(),
              file.name,
              this.valueInput.form!,
              this.getAttribute('lower-case-keys') === 'true',
              transform
            );
      if (result.error || result.data === null || !Array.isArray(result.data)) {
        this.fileInput.value = '';
        return;
      }
      this.valueInput.value = JSON.stringify(result.data);
      this.filenameInput.value = file.name;
      if (autoSubmit) {
        await this.querySelector('form-behavior')?.submit();
      }
    } finally {
      this.fileInput.disabled = false;
      this.fileInput.labels?.[0].classList.remove('btn-busy');
      this.fileInput.focus();
      dispatchChange(this.valueInput);
      dispatchChange(this.filenameInput);
    }
  }

  valueInputFocused() {
    this.fileInput.focus();
  }
}

declare global {
  interface Window {
    ExcelAttachmentElement: typeof ExcelAttachmentElement;
  }

  interface HTMLElementTagNameMap {
    'excel-attachment': ExcelAttachmentElement;
  }
}
